import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import clsx from 'clsx';

import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import { CardActions, ListItemIcon } from '@material-ui/core';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { RemoveCircleOutline, SettingsBackupRestore } from '@material-ui/icons';
import PaletteIcon from '@material-ui/icons/Palette';

import coffee from 'assets/coffee-cup.svg';
import { images } from 'assets';
import {
    BasicThreeDotsMenu,
    Text,
    PopupConfirmation,
    ColorSelector,
    StyledChip,
} from 'components/shared';
import {
    MenuType,
    OrderItemStatus,
    Positions,
    SpicinessLevels,
    Diets,
    DietaryLaws,
} from 'constants/enums';
import DishManagementDialog from './DishManagementDialog';
import { default as ordersStore } from 'store/ordersStore';
import { priceStyleHelper } from 'helpers/helperFunctions';
import { CatalogRestaurantDishModel, DishCreateModel } from 'api/models';
import menuDetailsStore from 'store/menuDetailsStore';
import { useAppSelector } from 'index';
import { theme } from 'config/theme';
import { useStyles } from '../styles';
import { DishData } from 'store/menuDetailsStore/reducer';
import { OrderDetailsItem } from 'store/ordersStore/reducer';
import DishDietaryView from 'components/shared/components/DishDietaryView';
import { getSpicinessLevels, getDiets, getDietaryLaws } from 'constants/maps';

interface DishPreviewItemProps {
    id: string;
    dish: DishData;
    isModal?: boolean;
    isSelected?: boolean;
    isDailyMenuDish?: boolean;
    isDisabled?: boolean;
    setSelected?: (id: string) => void;
    dailyMenuId?: string;
    sectionId: string;
    color?: string;
}

enum DishManagementDialogState {
    Hide,
    Copy,
    Edit,
}

enum DishActionConfirmationState {
    None,
    Enable,
    Disable,
    Remove,
    Delete,
    CatalogManagerDisableForRestaurant,
    CatalogManagerEnableForRestaurant,
    CatalogParticipantDisableForRestaurant,
    CatalogParticipantEnableForRestaurant,
}

export default function DishPreviewItem(props: DishPreviewItemProps) {
    const { dish, dailyMenuId } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const orders = useAppSelector((state) => state.orders);
    //const initialOrderItems = useAppSelector((state) => state.orders.initialOrderItems);
    const allergens = useAppSelector((state) => state.ingredients.allergies);
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            menu.catalogData?.allowToEditCatalog &&
            currentRestaurant?.currentPosition === Positions.MANAGER) ??
        false;

    const [showConfirmation, setShowConfirmation] = useState<DishActionConfirmationState>(
        DishActionConfirmationState.None
    );
    const [showEditDialog, setShowEditDialog] = useState<DishManagementDialogState>(
        DishManagementDialogState.Hide
    );
    const [showColorSelector, setShowColorSelector] = useState(false);
    const [color, setColor] = useState(props.color || dish.color || theme.palette.secondary.light);

    const targetItem = orders.itemsToAdd.find((x) => x.id === dish.id);

    const titles = new Map<DishActionConfirmationState, string>([
        [DishActionConfirmationState.Delete, i18n.t('common.delete')],
        [DishActionConfirmationState.Disable, i18n.t('common.disable')],
        [DishActionConfirmationState.Enable, i18n.t('common.enable')],
        [DishActionConfirmationState.Remove, i18n.t('common.remove')],
        [DishActionConfirmationState.CatalogManagerDisableForRestaurant, i18n.t('common.disable')],
        [
            DishActionConfirmationState.CatalogParticipantDisableForRestaurant,
            i18n.t('common.disable'),
        ],
        [DishActionConfirmationState.CatalogManagerEnableForRestaurant, i18n.t('common.enable')],
        [
            DishActionConfirmationState.CatalogParticipantEnableForRestaurant,
            i18n.t('common.enable'),
        ],
    ]);

    const descriptions = new Map<DishActionConfirmationState, string>([
        [DishActionConfirmationState.Delete, i18n.t('confirmation.deleteDish')],
        [DishActionConfirmationState.Disable, i18n.t('confirmation.disableItem')],
        [DishActionConfirmationState.Enable, i18n.t('confirmation.enableItem')],
        [DishActionConfirmationState.Remove, i18n.t('confirmation.deleteItem')],
        [
            DishActionConfirmationState.CatalogManagerDisableForRestaurant,
            i18n.t('confirmation.disableItem'),
        ],
        [
            DishActionConfirmationState.CatalogParticipantEnableForRestaurant,
            i18n.t('confirmation.enableItem'),
        ],
        [
            DishActionConfirmationState.CatalogParticipantDisableForRestaurant,
            i18n.t('confirmation.disableItem'),
        ],
        [
            DishActionConfirmationState.CatalogManagerEnableForRestaurant,
            i18n.t('confirmation.enableItem'),
        ],
    ]);

    const actions = new Map<DishActionConfirmationState, () => void>([
        [DishActionConfirmationState.Delete, deleteDish],
        [DishActionConfirmationState.Disable, disableDish],
        [DishActionConfirmationState.Enable, disableDish],
        [DishActionConfirmationState.Remove, removeDishFromSection],
        [
            DishActionConfirmationState.CatalogManagerDisableForRestaurant,
            handleDisableForRestaurant,
        ],
        [
            DishActionConfirmationState.CatalogParticipantDisableForRestaurant,
            handleDisableForRestaurant,
        ],
        [DishActionConfirmationState.CatalogManagerEnableForRestaurant, handleDisableForRestaurant],
        [
            DishActionConfirmationState.CatalogParticipantEnableForRestaurant,
            handleDisableForRestaurant,
        ],
    ]);

    function calculateItemPrice() {
        let price = props.dish.price;
        if (targetItem) {
            price = targetItem.price;
        }

        return price.toFixed(2);
    }

    function handleClick() {
        if (props.isModal && !!props.setSelected) {
            if (!props.isDisabled) {
                props.setSelected(dish.id);
                dispatch(ordersStore.actionCreators.setSelectedDish(dish.id, dailyMenuId));
            }
        } else {
            setShowEditDialog(DishManagementDialogState.Edit);
        }
    }

    const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
        setShowEditDialog(DishManagementDialogState.Hide);
        event?.stopPropagation();
    };

    function itemAmount() {
        return targetItem?.amount || 0;
    }

    function handleAmount(decrease: boolean = false) {
        const item: OrderDetailsItem = {
            price: props.dish.price,
            name: props.dish.name,
            id: props.dish.id,
            amount: 1,
            shortDescription: props.dish.shortDescription,
            orderId: orders.orderDetails.id,
            orderNumber: orders.orderDetails.orderNumber,
            comment: targetItem?.comment,
            service: orders.orderDetails.service,
            allergens: props.dish.allergens,
            ingredients: props.dish.ingredients,
            status: targetItem?.status ?? OrderItemStatus.Order,
            categoryId: '',
            categoryName: '',
            dailyMenuId: '',
            dishes: [],
            size: props.dish.size,
            additionalSizes: props.dish.additionalSizes,
        };
        dispatch(ordersStore.actionCreators.addItemToEditedOrder(item, decrease));
    }

    function removeDishFromSection() {
        dispatch(
            menuDetailsStore.actionCreators.removeDishFromSection({
                dishId: props.id,
                sectionId: props.sectionId,
                menuId: menu.id,
            })
        );
    }

    function disableDish() {
        dispatch(menuDetailsStore.actionCreators.disableDish(dish, props.sectionId));
    }

    function deleteDish() {
        if (menu.type === MenuType.Catalog) {
            dispatch(menuDetailsStore.actionCreators.deleteCatalogMenuDish(menu.id, props.id));
        } else {
            dispatch(menuDetailsStore.actionCreators.deleteDish(props.id, props.sectionId));
        }
    }

    function updateDish(model: DishCreateModel) {
        if (menu.type === MenuType.Catalog) {
            const catalogDishUpdateModel: CatalogRestaurantDishModel = {
                price: model.price,
                stock: model.stockQuantity,
                externalId: model.externalId ?? 0,
                supplierName: model.supplierName,
                applyToAll: isCatalogManager,
                workArea: model.categoryId,
            };

            dispatch(
                menuDetailsStore.actionCreators.restaurantMenuDetailsUpdateCatalogDish(
                    menu.catalogData!.catalogId,
                    currentRestaurant!.id,
                    props.dish.id,
                    catalogDishUpdateModel
                )
            );
            /* if (model.stockQuantity !== dish.stockQuantity) {
                dispatch(
                    menuDetailsStore.actionCreators.restaurantCatalogSetDishStockQuantity(
                        currentRestaurant!.id,
                        menu.catalogData!.catalogId,
                        props.dish.id,
                        model.stockQuantity
                    )
                );
            }
            if (model.categoryId !== dish.categoryId) {
                dispatch(
                    menuDetailsStore.actionCreators.restaurantCatalogSetDishWorkArea(
                        currentRestaurant!.id,
                        menu.catalogData!.catalogId,
                        props.dish.id,
                        model.categoryId
                    )
                );
            } */
        }

        if (menu.type !== MenuType.Catalog || isCatalogManager) {
            dispatch(
                menuDetailsStore.actionCreators.updateItem(
                    { ...model, catalogId: menu.catalogData?.catalogId },
                    props.dish.id,
                    props.sectionId
                )
            );
        }
    }

    function copyDish(model: DishCreateModel) {
        dispatch(menuDetailsStore.actionCreators.addNewItem(model));
    }

    async function handleDisableForRestaurant() {
        dispatch(
            menuDetailsStore.actionCreators.restaurantCatalogDishToggleDisable(
                currentRestaurant!.id,
                dish
            )
        );
    }

    const getThreeDotItems = () => {
        switch (menu.type) {
            case MenuType.Catalog: {
                if (!isCatalogManager) {
                    return [
                        <div
                            className={classes.threeDotsMenuItemContainer}
                            onClick={() =>
                                setShowConfirmation(
                                    dish.isDisabled
                                        ? DishActionConfirmationState.CatalogParticipantEnableForRestaurant
                                        : DishActionConfirmationState.CatalogParticipantDisableForRestaurant
                                )
                            }
                        >
                            <ListItemIcon>
                                {dish.isDisabled ? (
                                    <SettingsBackupRestore fontSize="small" color="disabled" />
                                ) : (
                                    <RemoveCircleOutline fontSize="small" color="disabled" />
                                )}
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {!dish.isDisabled
                                    ? i18n.t('common.disable')
                                    : i18n.t('common.enable')}
                            </Typography>
                        </div>,
                    ];
                }

                return [
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowEditDialog(DishManagementDialogState.Edit)}
                    >
                        <ListItemIcon>
                            <EditIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.editItem')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowColorSelector(true)}
                    >
                        <ListItemIcon>
                            <PaletteIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.changeColor')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowEditDialog(DishManagementDialogState.Copy)}
                    >
                        <ListItemIcon>
                            <img
                                src={images.icons.copy}
                                alt="copy"
                                height={18}
                                style={{ opacity: 0.4 }}
                            />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.copyItem')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={(e) => {
                            setShowConfirmation(
                                dish.catalogData!.isDisabled
                                    ? DishActionConfirmationState.Enable
                                    : DishActionConfirmationState.Disable
                            );
                        }}
                    >
                        <ListItemIcon>
                            {dish.catalogData?.isDisabled ? (
                                <SettingsBackupRestore fontSize="small" color="disabled" />
                            ) : (
                                <RemoveCircleOutline fontSize="small" color="disabled" />
                            )}
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {dish.catalogData?.isDisabled
                                ? i18n.t('restaurant.enableForRestaurant', {
                                      name: i18n.t('common.group.one'),
                                  })
                                : i18n.t('restaurant.disableForRestaurant', {
                                      name: i18n.t('common.group.one'),
                                  })}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() =>
                            setShowConfirmation(
                                dish.isDisabled
                                    ? DishActionConfirmationState.CatalogManagerEnableForRestaurant
                                    : DishActionConfirmationState.CatalogManagerDisableForRestaurant
                            )
                        }
                    >
                        <ListItemIcon>
                            {dish.isDisabled ? (
                                <SettingsBackupRestore fontSize="small" color="disabled" />
                            ) : (
                                <RemoveCircleOutline fontSize="small" color="disabled" />
                            )}
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {dish.isDisabled
                                ? i18n.t('restaurant.enableForRestaurant', {
                                      name: currentRestaurant?.name,
                                  })
                                : i18n.t('restaurant.disableForRestaurant', {
                                      name: currentRestaurant?.name,
                                  })}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowConfirmation(DishActionConfirmationState.Remove)}
                    >
                        <ListItemIcon>
                            <RemoveCircleIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {i18n.t('common.removeFromSection')}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowConfirmation(DishActionConfirmationState.Delete)}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.deleteItem')}</Typography>
                    </div>,
                ];
            }
            default:
                return [
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowEditDialog(DishManagementDialogState.Edit)}
                    >
                        <ListItemIcon>
                            <EditIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.editItem')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowColorSelector(true)}
                    >
                        <ListItemIcon>
                            <PaletteIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.changeColor')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowEditDialog(DishManagementDialogState.Copy)}
                    >
                        <ListItemIcon>
                            <img
                                src={images.icons.copy}
                                alt="copy"
                                height={18}
                                style={{ opacity: 0.4 }}
                            />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.copyItem')}</Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={(e) => {
                            setShowConfirmation(
                                dish.isDisabled
                                    ? DishActionConfirmationState.Enable
                                    : DishActionConfirmationState.Disable
                            );
                        }}
                    >
                        <ListItemIcon>
                            {dish.isDisabled ? (
                                <SettingsBackupRestore fontSize="small" color="disabled" />
                            ) : (
                                <RemoveCircleOutline fontSize="small" color="disabled" />
                            )}
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {!dish.isDisabled ? i18n.t('common.disable') : i18n.t('common.enable')}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowConfirmation(DishActionConfirmationState.Remove)}
                    >
                        <ListItemIcon>
                            <RemoveCircleIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {i18n.t('common.removeFromSection')}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setShowConfirmation(DishActionConfirmationState.Delete)}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.deleteItem')}</Typography>
                    </div>,
                ];
        }
    };

    function prepareAmountCounter() {
        return !props.isDailyMenuDish && (itemAmount() > 0 || props.isSelected) ? (
            <div className={classes.itemAmountContainer}>
                <div className={classes.itemAmountComponent}>
                    <div className={classes.itemAmountAction} onClick={() => handleAmount(true)}>
                        -
                    </div>
                    <div className={classes.itemAmountCount}>{itemAmount()}</div>
                    <div className={classes.itemAmountAction} onClick={() => handleAmount(false)}>
                        +
                    </div>
                </div>
            </div>
        ) : null;
    }

    function getAllergenIcons(dishAllergens: string[]) {
        return dishAllergens.map((id: string) => {
            const allergen = allergens.find((each) => each.id === id);
            return (
                <img
                    key={allergen?.id || Math.random()}
                    src={allergen?.icon}
                    alt={allergen?.name}
                    style={{ maxHeight: '100%', marginLeft: 7 }}
                />
            );
        });
    }

    function changeColor(newColor: string) {
        setColor(newColor);
    }

    function saveColor() {
        dispatch(
            menuDetailsStore.actionCreators.setSectionDishColor(
                props.sectionId,
                props.dish.id,
                !!props.dailyMenuId,
                color
            )
        );
    }

    function resetColor() {
        setColor(props.dish.color || theme.palette.secondary.light);
        setShowColorSelector(false);
    }

    useEffect(() => {
        setColor(props.color || dish.color || theme.palette.secondary.light);
    }, [props.color]);

    function preparePriceItem() {
        if (menu.type === MenuType.Daily) return null;

        if (dish.isCustomPrice) {
            return (
                <StyledChip
                    size="small"
                    color="primary"
                    labelFontSize={16}
                    labelFontWeight="bold"
                    label={i18n.t('common.customPrice')}
                />
            );
        }

        return (
            <IconButton>
                <div
                    className={clsx(classes.cardPrice, {
                        [classes.itemBlurred]: menu.type === MenuType.MultiCourse,
                    })}
                >
                    CHF{' '}
                    <label
                        className={clsx(classes.labelPrice, {
                            [classes.smallText]: dish.price.toString().length > 10,
                        })}
                    >
                        {priceStyleHelper(calculateItemPrice(), true)}
                    </label>
                </div>
            </IconButton>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            <Card
                elevation={0}
                className={clsx(classes.dishPreviewItemRoot, {
                    [classes.selected]: props.isSelected,
                })}
                /*  className={clsx(classes.root, {
                    [classes.selected]: props.isSelected,
                    [classes.forModal]: props.isModal,
                })} */
                style={{
                    opacity: isCatalogManager
                        ? dish.catalogData?.isDisabled
                            ? 0.5
                            : 1
                        : dish.isDisabled
                        ? 0.5
                        : 1,
                    position: 'relative',
                }}
                onClick={handleClick}
            >
                {dish.isDisabled && isCatalogManager && (
                    <div className={classes.cardHeaderLabel}>
                        <Text
                            text={i18n.t('catalog.disabledForRestaurantText', {
                                name: currentRestaurant?.name,
                            })}
                            maxLength={40}
                            fontSize={6}
                        />
                    </div>
                )}
                <CardHeader
                    avatar={
                        <Avatar aria-label="recipe">
                            <img
                                src={dish.photo ? dish.photo : coffee}
                                className={dish.photo ? classes.coverImg : ''}
                                alt="section_logo"
                            />
                        </Avatar>
                    }
                    action={
                        <>
                            {currentRestaurant?.currentPosition === Positions.MANAGER && (
                                <BasicThreeDotsMenu items={getThreeDotItems()} />
                            )}
                        </>
                    }
                    title={
                        <div>
                            <div className={classes.cardTitleContainer}>
                                <Text
                                    text={dish.name}
                                    className={classes.cardTitle}
                                    fontSize={18}
                                    maxLength={15}
                                />
                            </div>
                            <div className={classes.spicesContainer}>
                                <DishDietaryView<SpicinessLevels>
                                    value={dish.spicinessLevel}
                                    map={getSpicinessLevels()}
                                />
                                <DishDietaryView<Diets> value={dish.diet} map={getDiets()} />
                                {dish.labels.map((label) => (
                                    <IconButton key={label.id}>
                                        <img src={label.icon} alt={label.name} height={14} />
                                    </IconButton>
                                ))}
                                <DishDietaryView<DietaryLaws>
                                    value={dish.dietaryLaw}
                                    map={getDietaryLaws()}
                                />
                            </div>
                        </div>
                    }
                />
                <PopupConfirmation
                    open={showConfirmation > 0}
                    close={() => setShowConfirmation(DishActionConfirmationState.None)}
                    action={actions.get(showConfirmation)}
                    title={titles.get(showConfirmation) || ''}
                    description={descriptions.get(showConfirmation) || ''}
                    activeBtn={titles.get(showConfirmation) || ''}
                />
                <CardContent className={classes.cardContent}>
                    <Text text={dish.shortDescription} maxLength={70} whiteSpace="normal" />
                </CardContent>
                <CardActions disableSpacing className={classes.cardBottom}>
                    <div className={classes.spicesContainer}>
                        {getAllergenIcons(dish.allergens)}
                    </div>
                    {preparePriceItem()}
                </CardActions>
                {showEditDialog !== DishManagementDialogState.Hide && (
                    <DishManagementDialog
                        dish={
                            showEditDialog === DishManagementDialogState.Copy
                                ? { ...dish, id: '', catalogData: menu.catalogData }
                                : dish
                        }
                        sections={menu.sections}
                        action={
                            showEditDialog === DishManagementDialogState.Edit
                                ? updateDish
                                : copyDish
                        }
                        isOpen={true}
                        onClose={handleClose}
                        disabled={currentRestaurant?.currentPosition !== Positions.MANAGER}
                        forCopy={showEditDialog === DishManagementDialogState.Copy}
                    />
                )}
                <ColorSelector
                    isOpen={showColorSelector}
                    onClose={() => setShowColorSelector(false)}
                    value={color}
                    onChange={changeColor}
                    onSave={saveColor}
                    onCancel={resetColor}
                />
                <div style={{ height: 9, backgroundColor: color }}></div>
            </Card>
            {props.isModal && prepareAmountCounter()}
        </div>
    );
}
